<template>
  <div class="related-products">
    <div class="container">
      <!-- headline -->
      <div class="related-products__headline-wrapper">
        <div class="related-products__headline">
          {{ t.relatedProductsTitle }}
        </div>

        <!-- navigation - classes reused from all sliders -->
        <div v-if="showTabs" class="carousel__navigation" ref="navigation">
          <div
            ref="prev"
            class="navigation-button navigation-button--prev"></div>
          <div
            ref="next"
            class="navigation-button navigation-button--next"></div>
        </div>
      </div>

      <!-- content -->
      <div class="related-products__content">
        <!-- tabs titles -->
        <div
          v-if="showTabs"
          class="embla swiper paragraph-tabs__tab-links-container scroll scroll--start"
          ref="emblaThumbsNode">
          <div
            ref="emblaThumbsContainer"
            class="embla__container swiper-wrapper">
            <a
              v-for="groups in products"
              :key="groups.id"
              class="embla__slide paragraph-tabs__tab-link swiper-slide swiper-slide--active swiper-slide--to-right">
              {{ groups.group }}
            </a>
          </div>
        </div>

        <!-- tabs content -->
        <div class="related-products__tabs-content">
          <ul ref="emblaNode" class="embla swiper product-finder__products">
            <li class="embla__container swiper-wrapper">
              <div
                v-for="groups in products"
                :key="groups.id"
                class="embla__slide swiper-slide">
                <Product
                  v-for="product in groups.products"
                  :key="product.id"
                  :product="product"></Product>
              </div>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// embla slider
import AutoHeight from 'embla-carousel-auto-height';
import EmblaCarousel from 'embla-carousel';
// components
import Product from './Product';
// utils
import translationStrings from '../../translations/translationStrings';

export default {
  name: 'related-products',
  components: {
    Product,
  },
  props: {
    products: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      embla: null,
      emblaOptions: {
        inViewThreshold: 1,
      },
      emblaThumbs: null,
      emblaThumbOptions: {
        inViewThreshold: 1,
        containScroll: 'keepSnaps',
        dragFree: true,
      },
      t: translationStrings,
    };
  },
  computed: {
    showTabs() {
      return this.products.length > 1;
    },
  },
  methods: {
    addToggleThumbBtnsActive(emblaApiMain, emblaApiThumb) {
      // exit if there aren't any tab titles
      if (!emblaApiThumb) {
        return;
      }

      const slidesThumbs = emblaApiThumb.slideNodes();

      const toggleThumbBtnsState = () => {
        emblaApiThumb.scrollTo(emblaApiMain.selectedScrollSnap());
        const previous = emblaApiMain.previousScrollSnap();
        const selected = emblaApiMain.selectedScrollSnap();
        slidesThumbs[previous].classList.remove(
          'embla-thumbs__slide--selected',
        );
        slidesThumbs[selected].classList.add('embla-thumbs__slide--selected');
      };

      emblaApiMain.on('select', toggleThumbBtnsState);
      emblaApiThumb.on('init', toggleThumbBtnsState);

      return () => {
        const selected = emblaApiMain.selectedScrollSnap();
        slidesThumbs[selected].classList.remove(
          'embla-thumbs__slide--selected',
        );
      };
    },
    addThumbBtnsClickHandlers(emblaApiMain, emblaApiThumb) {
      // exit if there aren't any tab titles
      if (!emblaApiThumb) {
        return;
      }

      const slidesThumbs = emblaApiThumb.slideNodes();

      const scrollToIndex = slidesThumbs.map((_, index) => () => {
        if (emblaApiThumb.clickAllowed()) {
          emblaApiMain.scrollTo(index);
        }
      });

      slidesThumbs.forEach((slideNode, index) => {
        slideNode.addEventListener('click', scrollToIndex[index], false);
      });

      return () => {
        slidesThumbs.forEach((slideNode, index) => {
          slideNode.removeEventListener('click', scrollToIndex[index], false);
        });
      };
    },
    initSlideClasses() {
      // add selected class to slides in tab content slider
      const emblaSlides = this.embla.slideNodes();
      if (emblaSlides.length > 0) {
        emblaSlides[0].classList.add('embla__slide--selected');
      }

      // add selected class to slides in tab titles slider
      const emblaThumbs = this.emblaThumbs?.slideNodes();
      if (emblaThumbs && emblaThumbs.length > 0) {
        emblaThumbs[0].classList.add('embla__slide--selected');
      }
    },
    toggleSlideClasses() {
      const slides = this.embla.slideNodes();
      const previous = this.embla.previousScrollSnap();
      const selected = this.embla.selectedScrollSnap();
      slides[previous].classList.remove('embla__slide--selected');
      slides[selected].classList.add('embla__slide--selected');
    },
    toggleThumbAnimation() {
      const classRight = 'swiper-slide--to-right';
      const classLeft = 'swiper-slide--to-left';
      const classActive = 'embla-thumbs__slide--selected';
      const slides = this.emblaThumbs?.slideNodes();
      const activeSlide = slides.findIndex((slide) =>
        slide.classList.contains(classActive),
      );

      // handle transition classes based on slide direction
      slides.forEach((slide, index) => {
        slide.classList.remove(classLeft);
        slide.classList.remove(classRight);

        if (activeSlide > index) {
          slide.classList.add(classLeft);
        } else {
          slide.classList.add(classRight);
        }
      });
    },
    toggleGradient() {
      const { emblaThumbsNode } = this.$refs;
      const slidesInView = this.emblaThumbs?.slidesInView();
      const slides = this.embla.slideNodes();
      const lastSlideIndex = slides.length - 1;

      if (slides.length > 0 && !slidesInView.includes(0)) {
        emblaThumbsNode.classList.add('scroll--end');
      } else {
        emblaThumbsNode.classList.remove('scroll--end');
      }

      if (slides.length > 0 && !slidesInView.includes(lastSlideIndex)) {
        emblaThumbsNode.classList.add('scroll--start');
      } else {
        emblaThumbsNode.classList.remove('scroll--start');
      }
    },
    setupPrevNextBtns(prevBtn, nextBtn, embla) {
      prevBtn.addEventListener('click', embla.scrollPrev, false);
      nextBtn.addEventListener('click', embla.scrollNext, false);
    },
    disablePrevNextBtns(prevBtn, nextBtn, embla) {
      return () => {
        // handle enabled/disabled on prev button
        if (embla.canScrollPrev()) {
          prevBtn.removeAttribute('disabled');
          prevBtn.classList.remove('navigation-button--disabled');
        } else {
          prevBtn.setAttribute('disabled', 'disabled');
          prevBtn.classList.add('navigation-button--disabled');
        }

        // handle enabled/disabled on next button
        if (embla.canScrollNext()) {
          nextBtn.removeAttribute('disabled');
          nextBtn.classList.remove('navigation-button--disabled');
        } else {
          nextBtn.setAttribute('disabled', 'disabled');
          nextBtn.classList.add('navigation-button--disabled');
        }
      };
    },
  },
  mounted() {
    const { emblaNode, emblaThumbsNode, prev, next } = this.$refs;
    // init slider for tab content
    this.embla = EmblaCarousel(emblaNode, this.emblaOptions, [AutoHeight()]);

    // init slider for tab titles, but only if more than 1
    if (this.showTabs) {
      this.emblaThumbs = EmblaCarousel(emblaThumbsNode, this.emblaThumbOptions);

      this.setupPrevNextBtns(prev, next, this.embla);
    }

    // update on resize
    const toggleEmblaReady = (event) => {
      const isResizeEvent = event === 'resize';
      const toggleClass = isResizeEvent ? 'remove' : 'add';
      emblaNode.classList[toggleClass]('embla--is-ready');

      if (isResizeEvent) {
        this.embla.reInit();
      }
    };

    // reset
    const removeThumbBtnsClickHandlers = this.addThumbBtnsClickHandlers(
      this.embla,
      this.emblaThumbs,
    );
    const removeToggleThumbBtnsActive = this.addToggleThumbBtnsActive(
      this.embla,
      this.emblaThumbs,
    );
    const disablePrevAndNextBtns = this.disablePrevNextBtns(
      prev,
      next,
      this.embla,
    );

    // add events for tab contents
    this.embla
      .on('init', this.initSlideClasses)
      .on('select', this.toggleSlideClasses)
      .on('resize', toggleEmblaReady)
      .on('reInit', toggleEmblaReady);

    // add events for tab titles
    if (this.emblaThumbs) {
      this.embla
        .on('init', disablePrevAndNextBtns)
        .on('select', disablePrevAndNextBtns)
        .on('select', this.toggleThumbAnimation)
        .on('destroy', removeThumbBtnsClickHandlers)
        .on('destroy', removeToggleThumbBtnsActive);

      this.emblaThumbs
        .on('settle', this.toggleGradient)
        .on('destroy', removeThumbBtnsClickHandlers)
        .on('destroy', removeToggleThumbBtnsActive);
    }

    this.embla.internalEngine().translate.toggleActive(false);
    this.embla.internalEngine().translate.clear();

    this.$nextTick(() => {
      this.$refs.emblaNode.classList.add('embla--is-ready');
    });
  },
};
</script>
